
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Bell, BellOff, Smartphone, Loader2, UserX } from 'lucide-react';
import { useOneSignal } from '@/hooks/useOneSignal';
import { useAuth } from '@/contexts/AuthContext';

const OneSignalSettings = () => {
  const { user } = useAuth();
  const {
    isInitialized,
    isSubscribed,
    isLoading,
    hasCheckedInitialState,
    requestPermission,
    unsubscribe
  } = useOneSignal();

  console.log('[OneSignalSettings] Estado actual:', {
    user: !!user,
    isInitialized,
    isSubscribed,
    isLoading,
    hasCheckedInitialState
  });

  const handleToggleNotifications = async (checked: boolean) => {
    console.log('[OneSignalSettings] Toggle clicked:', checked);
    
    if (checked) {
      console.log('[OneSignalSettings] Activando notificaciones...');
      const success = await requestPermission();
      if (success) {
        console.log('[OneSignalSettings] Notificaciones activadas exitosamente');
        window.dispatchEvent(new CustomEvent('notifications-enabled'));
      }
    } else {
      console.log('[OneSignalSettings] Desactivando notificaciones...');
      await unsubscribe();
    }
  };

  // Si no hay usuario autenticado
  if (!user) {
    return (
      <Card className="border-gray-200 bg-gray-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-600">
            <UserX className="h-5 w-5" />
            Notificaciones Push
          </CardTitle>
          <CardDescription className="text-gray-500">
            Inicia sesión para activar las notificaciones push
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">
            Las notificaciones push están disponibles después de iniciar sesión en tu cuenta.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Si OneSignal no está disponible
  if (!isInitialized && hasCheckedInitialState) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <BellOff className="h-5 w-5" />
            Notificaciones Push No Disponibles
          </CardTitle>
          <CardDescription className="text-amber-700">
            Tu navegador no soporta notificaciones push o hay un problema de conexión.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-amber-600">
            Intenta recargar la página o usar un navegador compatible con notificaciones push.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Mostrar cargando mientras se verifica el estado inicial
  if (!hasCheckedInitialState) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="text-vegana-primary" />
            Notificaciones Push
          </CardTitle>
          <CardDescription>
            Verificando estado de las notificaciones...
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-vegana-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="text-vegana-primary" />
          Notificaciones Push
        </CardTitle>
        <CardDescription>
          Recibe consejos nutricionales y recordatorios directamente en tu dispositivo
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="space-y-1">
            <Label className="text-base font-medium">
              Activar notificaciones
            </Label>
            <p className="text-sm text-gray-500">
              Recibe consejos de nutrición y recordatorios personalizados
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isLoading && <Loader2 className="h-4 w-4 animate-spin text-vegana-primary" />}
            <Switch
              checked={isSubscribed}
              onCheckedChange={handleToggleNotifications}
              disabled={isLoading || !isInitialized}
            />
          </div>
        </div>

        {Notification.permission === 'denied' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <BellOff className="h-4 w-4 text-red-600" />
              <p className="text-sm text-red-800 font-medium">
                Permisos de notificación denegados
              </p>
            </div>
            <p className="text-sm text-red-600 mb-3">
              Para recibir notificaciones, debes activar los permisos en la configuración de tu navegador.
            </p>
            <div className="text-sm text-red-600">
              <p className="font-medium mb-1">Cómo activar:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Haz clic en el icono del candado en la barra de direcciones</li>
                <li>Selecciona "Permitir" para las notificaciones</li>
                <li>Recarga la página e intenta de nuevo</li>
              </ul>
            </div>
          </div>
        )}

        {isSubscribed && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Bell className="h-4 w-4 text-green-600" />
              <p className="text-sm text-green-800 font-medium">
                Notificaciones activadas
              </p>
            </div>
            <p className="text-sm text-green-600">
              Recibirás consejos nutricionales, recordatorios de comidas y actualizaciones sobre nuevas recetas directamente en tu dispositivo.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <h4 className="font-medium text-sm">¿Qué notificaciones recibirás?</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Consejos nutricionales personalizados</li>
            <li>• Recordatorios de comidas y hidratación</li>
            <li>• Nuevas recetas recomendadas para ti</li>
            <li>• Actualizaciones importantes de la app</li>
          </ul>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800 font-medium mb-1">Debug Info:</p>
            <div className="text-xs text-blue-600 space-y-1">
              <p>OneSignal Initialized: {isInitialized ? 'Sí' : 'No'}</p>
              <p>Subscribed: {isSubscribed ? 'Sí' : 'No'}</p>
              <p>Loading: {isLoading ? 'Sí' : 'No'}</p>
              <p>Has Checked: {hasCheckedInitialState ? 'Sí' : 'No'}</p>
              <p>Browser Permission: {Notification.permission}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OneSignalSettings;
